import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import { Fade } from '@material-ui/core';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import ORTypography from 'components/ORTypography/ORTypography';
import LinearProgress from '@material-ui/core/LinearProgress';
import { Field, reduxForm, SubmissionError } from 'redux-form';
import { uiState } from 'react-redux-ui-state';
import MorePropTypes from 'airbnb-prop-types';
import zxcvbn from 'zxcvbn';

import WrappedTextField from 'components/WrappedTextField/WrappedTextField';
import RBACSubmitButton from 'components/RBACWrapper/RBACSubmitButton/RBACSubmitButton';
import { OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER } from 'constants/roles';
import Fab from '@material-ui/core/Fab';
import Close from '@material-ui/icons/Close';
import { required, maxLength } from '../../services/validationMsgs';

const propTypes = {
  addInstanceUser: PropTypes.func.isRequired,
  instanceId: PropTypes.string.isRequired,
  instanceService: PropTypes.string.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  submitSucceeded: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  valid: PropTypes.bool.isRequired,
  pristine: PropTypes.bool.isRequired,
  error: MorePropTypes.or([MorePropTypes.explicitNull, PropTypes.string, PropTypes.shape({})]),
  setUiState: PropTypes.func.isRequired,
  selectedRole: PropTypes.string.isRequired,
  passwordLength: PropTypes.number.isRequired,
  strength: PropTypes.shape({}).isRequired,
  classes: PropTypes.shape({}).isRequired,
  closePopout: MorePropTypes.or([MorePropTypes.explicitNull, PropTypes.func]),
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  reset: PropTypes.func.isRequired,
  selectedDatabase: PropTypes.string.isRequired,
  databases: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  parentDb: PropTypes.string,
};

const defaultProps = {
  parentDb: '',
  error: null,
  closePopout: null,
};

const handleFormSubmissionReturn = (result) => {
  if (result.error) {
    throw new SubmissionError(result.formErrors);
  }
};

const maxLength128 = maxLength(128);

export function UnstyledAddUserForm({
  addInstanceUser,
  instanceId,
  instanceService,
  handleSubmit,
  submitSucceeded,
  submitting,
  valid,
  pristine,
  error,
  setUiState,
  selectedRole,
  passwordLength,
  strength,
  closePopout,
  classes,
  userRoles,
  reset,
  selectedDatabase,
  databases,
  parentDb,
}) {
  const isCockroachDB = instanceService === 'CockroachDB';
  const isMongoDB = instanceService === 'MongoDB';

  const submitHandler = (data) => {
    // If the instance is Mongo and there's a parentdb from the DBs tab, newDb is parentdb
    // If the instance is Mongo and a DB is selected from the Users tab, newDb is selectedDatabase
    // If the instance is not Mongo, newDb will be 0, the default value for selectedDatabase. That will be
    // garbage data for the addInstanceUser call and that's ok.
    const newDb = parentDb || selectedDatabase;

    const newUser = { ...data, roles: [selectedRole], database: newDb };
    return addInstanceUser(instanceId, instanceService, newUser).then(handleFormSubmissionReturn);
  };

  const messageDictionary = {
    0: 'Awful',
    1: 'Weak',
    2: 'Mediocre',
    3: 'Good',
    4: 'Strong',
  };

  const passwordStrengthCheck = (event, password) => {
    setUiState({ passwordLength: password ? password.length : 0 });
    const { score } = zxcvbn(password);
    setUiState({
      strength: {
        score,
        message: password ? messageDictionary[score] : 'None',
      },
    });
  };

  function handleCancel() {
    reset();
    passwordStrengthCheck(null, '');
  }

  const errorElm = error ? (
    <ORTypography color="error" className={classes.formStatus}>
      {error}
    </ORTypography>
  ) : null;

  if (submitSucceeded && closePopout) {
    closePopout();
  }

  const DB_USER_ROLES = {
    Elasticsearch: ['admin', 'kibana', 'readonly'],
    PostgreSQL: ['admin'],
    TimescaleDB: ['admin'],
    MongoDB: ['read', 'readWrite'],
    // The admin role for CRDB is a placeholder until there are actual roles in the API
    CockroachDB: ['admin'],
  };

  const StrengthMessage = (
    <ORTypography
      align="left"
      color="textPrimary"
      className={classes.strengthMeterText}
      classes={{ colorTextPrimary: classes[`${strength.message}Text`] }}
    >
      {strength.message}
    </ORTypography>
  );

  const roleOptions = DB_USER_ROLES[instanceService].map((role) => (
    <option key={role} value={role}>
      {role}
    </option>
  ));

  const databaseOptions = parentDb ? (
    <option key={parentDb} value={parentDb}>
      {parentDb}
    </option>
  ) : (
    databases.map((database) => (
      <option key={database.db} value={database.db}>
        {database.db}
      </option>
    ))
  );

  const noDatabases = databases.length === 0 && !parentDb;

  const handleChange = (event) => {
    setUiState({ selectedRole: event.target.value });
  };

  return (
    <React.Fragment>
      <ORTypography className={classes.header} component="h2" variant="h5">
        Add a User
      </ORTypography>
      <form onSubmit={handleSubmit(submitHandler)}>
        <div className={classes.fieldContainer}>
          <Field
            name="username"
            variant="outlined"
            label="Username"
            placeholder="admin"
            component={WrappedTextField}
            validate={[maxLength128, required]}
            props={{ InputLabelProps: { shrink: true, className: classes.inputLabel } }}
            fullWidth
          />
          <Field
            name="password"
            variant="outlined"
            label="Password"
            placeholder="**********"
            className={classes.userInput}
            component={WrappedTextField}
            type="password"
            validate={isCockroachDB ? [maxLength128] : [maxLength128, required]}
            onChange={passwordStrengthCheck}
            props={{ InputLabelProps: { shrink: true, className: classes.inputLabel } }}
            fullWidth
          />
          {isCockroachDB && (
            <ORTypography align="left" component="p" className={classes.noPasswordNeeded}>
              <i>If no password is provided, this user must connect with client certificates.</i>
            </ORTypography>
          )}
          {passwordLength > 0 && (
            <Fade in={passwordLength > 0}>
              <div className={classes.strengthMeterContainer}>
                <LinearProgress
                  classes={{
                    colorPrimary: classes.colorPrimary,
                    barColorPrimary: classes[strength.message],
                    root: classes.strengthMeter,
                  }}
                  variant="determinate"
                  value={strength.message === 'Awful' ? 5 : strength.score * 25}
                />
                <ORTypography align="left" component="p" className={classes.strengthMeterText}>
                  Password Strength: {StrengthMessage}
                </ORTypography>
              </div>
            </Fade>
          )}
          {!isCockroachDB && (
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel id="demo-simple-select-outlined-label">Roles</InputLabel>
              <Select
                labelId="demo-simple-select-outlined-label"
                id="demo-simple-select-outlined"
                value={selectedRole}
                onChange={handleChange}
                label="Roles"
                native
              >
                <option disabled aria-label="" value="0">
                  Select Your Role
                </option>
                {roleOptions}
              </Select>
            </FormControl>
          )}
          {isMongoDB && !parentDb && (
            <React.Fragment>
              <FormControl variant="outlined" className={classes.formControl}>
                <InputLabel id="demo-simple-select-outlined-two-label">Database</InputLabel>
                <Select
                  labelId="demo-simple-select-outlined-two-label"
                  id="demo-simple-select-outlined-two"
                  value={selectedDatabase}
                  label="Database"
                  native
                  onChange={(e) => {
                    setUiState({ selectedDatabase: e.target.value });
                  }}
                  disabled={noDatabases}
                >
                  <option disabled aria-label="Select Your Database" value="0">
                    Select Your Database
                  </option>
                  {databaseOptions}
                </Select>
              </FormControl>
              {noDatabases && (
                <ORTypography align="left" component="p" className={classes.noDatabases}>
                  <i>
                    You have no Databases setup yet. Please create one now before adding a User.
                  </i>
                </ORTypography>
              )}
            </React.Fragment>
          )}
        </div>
        {errorElm}
        <div className={classes.addButtonContainer}>
          <Fab
            size="small"
            color="secondary"
            className={classes.cancelButton}
            onClick={handleCancel}
          >
            <Close fontSize="large" />
          </Fab>
          <RBACSubmitButton
            userAccess={userRoles}
            allowedAccess={[OWNER, ADMIN, OR_ADMIN, OR_SUPERUSER]}
            submitting={submitting}
            disabled={isCockroachDB ? !valid || pristine : !valid || pristine || !selectedRole}
          />
        </div>
      </form>
    </React.Fragment>
  );
}

UnstyledAddUserForm.propTypes = propTypes;
UnstyledAddUserForm.defaultProps = defaultProps;

const styles = (theme) => ({
  formControl: {
    width: '100%',
    marginTop: '16px',
  },
  select: {
    width: '100%',
  },
  header: {
    margin: '1rem',
  },
  fieldContainer: {
    margin: '1rem',
    '& input': {
      textAlign: 'left',
    },
  },
  roleContainer: {
    textAlign: 'center',
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'column',
    flexBasis: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    margin: theme.spacing(2),

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
    },
  },
  addButtonContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  addButton: {
    margin: theme.spacing(2),
  },
  cancelButton: {
    margin: theme.spacing(2),
  },
  strengthMeterContainer: {
    top: theme.spacing(2.25),
    marginTop: theme.spacing(1),
    zIndex: 10,
    display: 'flex',
    flexDirection: 'column',
  },
  strengthMeter: {
    flexGrow: 1,
  },
  noPasswordNeeded: {
    paddingTop: 8,
    display: 'block',
    fontSize: 12,
    fontStyle: 'italic, Roboto Regular',
    color: theme.customColors.darkGrey,
  },
  noDatabases: {
    color: theme.customColors.red,
    fontStyle: 'italic',
    fontSize: '.8rem',
    marginTop: theme.spacing(1),
  },
  strengthMeterText: {
    marginTop: theme.spacing(1),
    fontSize: '.8rem',
  },
  colorPrimary: {
    backgroundColor: '#eee0',
  },
  NoneText: {
    display: 'inline',
  },
  Awful: {
    backgroundColor: theme.customColors.red,
  },
  AwfulText: {
    display: 'inline',
    color: theme.customColors.red,
  },
  Weak: {
    backgroundColor: theme.customColors.red,
  },
  WeakText: {
    display: 'inline',
    color: theme.customColors.red,
  },
  Mediocre: {
    backgroundColor: theme.customColors.burntYellow,
  },
  MediocreText: {
    display: 'inline',
    color: theme.customColors.burntYellow,
  },
  Good: {
    backgroundColor: theme.customColors.lightGreen,
  },
  GoodText: {
    display: 'inline',
    color: theme.customColors.lightGreen,
  },
  Strong: {
    backgroundColor: '#008100',
  },
  StrongText: {
    display: 'inline',
    color: '#008100',
  },
  inputLabel: {
    color: theme.palette.text.primary,
  },
  formStatus: {
    margin: `${theme.spacing(2)}px auto auto auto`,
    fontSize: '.8rem',
    fontStyle: 'italic, Roboto Regular',
    maxWidth: '300px',
  },
  userInput: {
    marginTop: '16px',
  },
});

export const uiConfig = {
  name: 'instanceUserFormItem',
  state: () => ({
    selectedDatabase: '0',
    selectedRole: '0',
    passwordLength: 0,
    strength: {
      score: 0,
      message: 'None',
    },
  }),
};

export default uiState(uiConfig)(
  withStyles(styles)(reduxForm({ form: 'newUser' })(UnstyledAddUserForm)),
);
